import { useEffect, useState } from "react";
import Card, { CardColor } from "../../components/Card/Card";
import { useNavigate } from "react-router-dom";
import MainCard from "../../components/MainCard/MainCard";
import { colors } from "../../constants/colors";
import "./styles.scss";
import { homeStore } from "../../store/HomeStore";
import Spinner from "../../components/Spinner/Spinner";
import { ROUTE_PATHS } from "../../constants/routePaths";
import { Box, Link, SvgIconTypeMap, Typography } from "@mui/material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SsidChartOutlined from "@mui/icons-material/SsidChartOutlined";
import AttachMoneyOutlined from "@mui/icons-material/AttachMoneyOutlined";
import ImportantDevicesOutlined from "@mui/icons-material/ImportantDevicesOutlined";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import ISsidChartArrow from "../../assets/svg/ISsidChartArrow";

interface Card {
  title: string;
  primaryText?: string | number;
  secondPrimaryText?: string | number;
  secondaryText: string;
  secondSecondaryText?: string;
  path: string;
  secondaryPath: string;
  color: CardColor;
  id: string;
  customClick?: boolean;
  icon: OverridableComponent<SvgIconTypeMap>;
}

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  let navigate = useNavigate();

  const handleClick = (path: string, secondaryPath: string, clientType: string) => {
    if (clientType === "VIP" && !!secondaryPath) {
      return navigate(secondaryPath, { state: { clientType: clientType } });
    }
    navigate(path, { state: { clientType: clientType } });
  };

  const cardsData: Card[] = [
    {
      title: "My Leads This Month",
      primaryText: homeStore.coach?.countLeadClients || "0",
      secondPrimaryText: homeStore.coach?.countLeadCoaches || "0",
      secondaryText: "Client",
      secondSecondaryText: "Coach",
      path: ROUTE_PATHS.MY_CLIENT_LEADS,
      secondaryPath: ROUTE_PATHS.MY_COACH_LEADS,
      color: "primary",
      id: "home-card-my-leads",
      customClick: true,
      icon: FilterListOutlinedIcon,
    },
    {
      title: "My Clients",
      primaryText: homeStore.coach?.countClients || "0",
      secondPrimaryText: homeStore.coach?.countClientsVIP || "0",
      secondaryText: "This Month",
      secondSecondaryText: "Total VIP",
      path: ROUTE_PATHS.MY_CLIENTS,
      secondaryPath: "",
      color: "primary",
      id: "home-card-my-clients",
      icon: PeopleAltOutlinedIcon,
    },
    {
      title: "Client Reports",
      secondaryText: isMobile ? "Track Client Activity" : "Additional Filters for My Clients",
      path: ROUTE_PATHS.CLIENT_REPORTS,
      secondaryPath: "",
      color: "secondary",
      id: "home-card-my-client-reports",
      icon: AssignmentIndOutlinedIcon,
    },
    // {
    //   title: "New App Clients",
    //   secondaryText: "New app launched Oct 1",
    //   path: ROUTE_PATHS.NEW_APP_CLIENTS,
    //   secondaryPath: "",
    //   color: "secondary",
    //   id: "home-card-new-app-clients",
    //   icon: ISsidChartArrow,
    // },
    {
      title: isMobile ? "Training" : "Training Portal",
      secondaryText: "Program Information",
      path: ROUTE_PATHS.TRAINING_PORTAL,
      secondaryPath: "",
      color: "secondary",
      id: "home-card-training-portal",
      icon: ImportantDevicesOutlined,
    },
    {
      title: "Back Office",
      secondaryText: "Commissions & Payments",
      path: ROUTE_PATHS.PAP,
      secondaryPath: "",
      color: "secondary",
      id: "home-card-pap",
      icon: AttachMoneyOutlined,
    },
    {
      title: "Energy Chart",
      secondaryText: "Marketing Strategy",
      path: ROUTE_PATHS.ENERGY_CHART,
      secondaryPath: "",
      color: "secondary",
      id: "home-card-energy-chart",
      icon: SsidChartOutlined,
    },
  ];

  useEffect(() => {
    const fetchHome = async () => {
      await homeStore.fetchHome();
      setLoading(false);
    };

    fetchHome();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <MainCard backgroundColor={colors.background.main} minHeight="800px">
      {loading ? (
        <Spinner size={50} sx={{ position: "absolute", top: "45%", left: "50%" }} />
      ) : (
        <>
          <Box sx={{ mb: 4, textAlign: "center", display: "flex", justifyContent: "center" }}>
            <ArrowCircleRightOutlinedIcon
              sx={{
                color: "#686A6B",
                fontSize: "2.4rem",
                marginRight: 0.4,
              }}
            />
            <Typography variant="body1" sx={{ color: "#333" }}>
              Check out the latest{" "}
              <Link
                href="https://www.fasterwaytofatloss.com/coach-newsletter"
                sx={{ color: colors.blue.two, fontWeight: "bold" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Newsletter
              </Link>{" "}
              and stay up to date with the{" "}
              <Link
                href="https://www.fasterwaytofatloss.com/coach-calendar"
                sx={{ color: colors.blue.two, fontWeight: "bold" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Coach Calendar
              </Link>
            </Typography>
          </Box>
          <div className="home">
            {cardsData.map((card, index) => (
              <Card
                className="card"
                id={card.id}
                key={index}
                title={card.title}
                primaryText={card.primaryText}
                secondPrimaryText={card.secondPrimaryText}
                secondaryText={card.secondaryText}
                secondSecondaryText={card.secondSecondaryText}
                onClick={(clientType) => handleClick(card.path, card.secondaryPath, clientType)}
                color={card.color}
                customClick={card.customClick}
                icon={card.icon}
              />
            ))}
          </div>
          <Box sx={{ mt: isMobile ? 4 : "auto", pt: isMobile ? 0 : 4, textAlign: "center" }}>
            <Typography variant="body1" sx={{ color: "#333" }}>
              Have new ideas or suggestions for Coach Connect? Email{" "}
              <Link
                href="mailto:cc_feedback@fasterwaytofatloss.com"
                sx={{ color: colors.blue.two, fontWeight: "bold" }}
              >
                cc_feedback@fasterwaytofatloss.com
              </Link>
            </Typography>
          </Box>
        </>
      )}
    </MainCard>
  );
};

export default Home;
